import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const useVideoLoaded = (
  ref: React.MutableRefObject<null | HTMLVideoElement>,
  pre?: React.MutableRefObject<null | HTMLVideoElement>
) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!ref.current || loaded) return;
    let timer: number;
    const checkLoaded = () => {
      timer = window.setTimeout(() => {
        if (ref.current && ref.current.readyState === 4) {
          ref.current.currentTime =
            ((pre && pre.current && pre.current.currentTime) || 0) + 0.4;
          timer = window.setTimeout(() => setLoaded(true), 350);
          return;
        }
        return checkLoaded();
      }, 500);
    };
    checkLoaded();
    return () => clearTimeout(timer);
  }, [ref, loaded]);

  return loaded;
};

const ProgressiveVideo = ({ pre, video }: { pre: string; video: string }) => {
  const videoRef = useRef(null);
  const preRef = useRef(null);
  const preLoaded = useVideoLoaded(preRef);
  const videoLoaded = useVideoLoaded(videoRef, preRef);

  return (
    <Wrapper loaded={videoLoaded} preLoaded={preLoaded}>
      <img src="data:image/png;base64,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" />
      <video loop muted autoPlay playsInline src={pre} ref={preRef} />
      <video loop muted autoPlay playsInline src={video} ref={videoRef} />
    </Wrapper>
  );
};

const Wrapper = styled.div<{ preLoaded: boolean; loaded: boolean }>`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: -3;
  filter: ${props => (props.loaded ? "blur(0px)" : "blur(3px)")};

  video,
  img {
    width: 100%;
    position: absolute;
  }

  img {
    opacity: ${props => (props.preLoaded ? 0 : 1)};
    transition: 0.3s;
  }

  & > video:nth-child(2) {
    opacity: ${props => (props.preLoaded && !props.loaded ? 1 : 0)};
    transition: 0.3s;
  }
  & > video:nth-child(3) {
    opacity: ${props => (props.loaded ? 1 : 0)};
    transition: 0.3s;
  }
`;

export default ProgressiveVideo;
